import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ANALYTICS } from './analyticsTokens';
import {
  COVERAGE_FIELDS,
  COVERAGE_FILTERS,
  COVERAGE_SOURCE_LABELS,
  coverageCell,
  coverageCellText,
  coverageSummary,
  coverageTint,
  describeCoverageHeader,
  describeCoverageSources,
  describeSharedColumns,
  placeholderNamesLine,
  filterCoverage,
  rankCoverage,
  share,
  type AuditDistrict,
  type CoverageFilter,
  type CoverageGroup,
  type CoverageRow,
  type CoverageTint,
} from './progressReportAuditApi';

type Props = {
  districts: AuditDistrict[];
  onOpenDetails: (districtId: string) => void;
};

const base = { fontFamily: ANALYTICS.font } as const;
const cardSx = { bgcolor: ANALYTICS.surface, borderRadius: '8px', border: `1px solid ${ANALYTICS.grey50}`, p: '24px', overflowX: 'auto' } as const;
const lineSx = { ...base, fontSize: 14, lineHeight: '20px', color: ANALYTICS.muted } as const;
const captionSx = { ...base, fontSize: 12, lineHeight: '16px', color: ANALYTICS.muted } as const;
const eyebrowSx = { ...base, fontSize: 12, lineHeight: '16px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: ANALYTICS.muted } as const;
const controlSx = { ...base, fontSize: 14, lineHeight: '20px', height: 40, border: `1px solid ${ANALYTICS.grey100}`, borderRadius: '8px', bgcolor: ANALYTICS.surface, color: ANALYTICS.ink, p: '0 12px' } as const;
const cellSx = { ...base, fontSize: 14, lineHeight: '20px', width: 56, flexShrink: 0, display: 'flex', alignItems: 'center', p: '0 4px', height: 44, fontVariantNumeric: 'tabular-nums' } as const;
const chevronSx = { width: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 44, color: ANALYTICS.muted } as const;
const hiddenSx = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' } as const;

const TINT_COLORS: Record<CoverageTint, { color: string; bgcolor?: string }> = {
  red: { color: ANALYTICS.red600, bgcolor: ANALYTICS.red100 },
  yellow: { color: ANALYTICS.yellow900, bgcolor: ANALYTICS.yellow100 },
  ok: { color: ANALYTICS.ink },
};

const GROUP_LABELS: Record<CoverageGroup, string> = { every: 'Expected for every student', some: 'Expected for some students' };

function fmt(n: number): string {
  return n.toLocaleString('en-US');
}

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

function Summary({ rows, uncounted }: { rows: CoverageRow<AuditDistrict>[]; uncounted: AuditDistrict[] }) {
  const summary = coverageSummary(rows);
  const dot = (color: string) => <Box component="i" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '5px', bgcolor: color, flexShrink: 0 }} />;
  const itemSx = { display: 'flex', gap: '8px', alignItems: 'center' } as const;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Typography component="h2" data-testid="coverage-summary-head" sx={{ ...base, m: 0, fontSize: 20, lineHeight: '28px', fontWeight: 700, color: ANALYTICS.ink }}>
        {plural(summary.districts, 'district')} audited
      </Typography>
      <Box data-testid="coverage-summary" sx={{ ...lineSx, display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', color: ANALYTICS.ink }}>
        <Box component="span" sx={{ ...itemSx, fontWeight: 700 }}>
          {dot(ANALYTICS.red600)}
          {`${summary.red} missing a required field`}
        </Box>
        <Box component="span" sx={itemSx}>
          {dot(ANALYTICS.yellow900)}
          {`${summary.yellow} thin or missing an optional field`}
        </Box>
        <Box component="span" sx={{ ...itemSx, color: ANALYTICS.muted }}>
          {dot(ANALYTICS.grey100)}
          {`${summary.clean} complete`}
        </Box>
      </Box>
      {uncounted.length > 0 && (
        <Typography data-testid="coverage-uncounted" sx={lineSx}>
          {plural(uncounted.length, 'district')} {uncounted.length === 1 ? 'has' : 'have'} no coverage counts and {uncounted.length === 1 ? 'is' : 'are'} not in the grid (audited before coverage
          was counted, not reached by the run, or not audited): {uncounted.map((row) => row.districtName || row.districtId).join(', ')}
        </Typography>
      )}
    </Box>
  );
}

function Controls({ query, filter, onQuery, onFilter }: { query: string; filter: CoverageFilter; onQuery: (q: string) => void; onFilter: (f: CoverageFilter) => void }) {
  return (
    <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
      <Box
        component="input"
        type="search"
        data-testid="coverage-search"
        aria-label="Find a district"
        placeholder="Find a district"
        value={query}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onQuery(event.target.value)}
        sx={{ ...controlSx, width: 280 }}
      />
      <Box
        component="select"
        data-testid="coverage-filter"
        aria-label="Which districts to show"
        value={filter}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onFilter(event.target.value as CoverageFilter)}
        sx={{ ...controlSx, fontWeight: 500, cursor: 'pointer' }}
      >
        {COVERAGE_FILTERS.map((option) => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </Box>
    </Box>
  );
}

function GridHead() {
  return (
    <Box data-testid="coverage-grid-head" sx={{ display: 'flex', alignItems: 'flex-end', pb: '8px', borderBottom: `1px solid ${ANALYTICS.ink}` }}>
      <Box sx={{ ...eyebrowSx, flex: 1, minWidth: 180 }}>District · Students</Box>
      {COVERAGE_FIELDS.map((field) => (
        <Box key={field.key} data-testid="coverage-head-cell" sx={{ ...cellSx, height: 'auto', fontSize: 12, lineHeight: '16px', fontWeight: 700, color: ANALYTICS.muted }}>
          {field.short}
        </Box>
      ))}
      <Box sx={{ ...chevronSx, height: 'auto' }} />
    </Box>
  );
}

export function Chevron({ open, height }: { open: boolean; height?: number }) {
  return (
    <Box component="span" sx={{ ...chevronSx, height: height ?? chevronSx.height, color: open ? ANALYTICS.primary : ANALYTICS.muted }}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : undefined }}>
        <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </Box>
  );
}

function Row({ entry, open, onToggle }: { entry: CoverageRow<AuditDistrict>; open: boolean; onToggle: () => void }) {
  const { row } = entry;
  return (
    <Box
      component="button"
      type="button"
      data-testid="coverage-row"
      data-district={row.districtId}
      aria-expanded={open}
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        textAlign: 'left',
        p: 0,
        border: 0,
        borderBottom: `1px solid ${open ? 'transparent' : ANALYTICS.grey50}`,
        bgcolor: open ? ANALYTICS.indigo50 : 'transparent',
        cursor: 'pointer',
        '&:hover': { bgcolor: open ? ANALYTICS.indigo50 : ANALYTICS.actionHover },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', py: '8px' }}>
        <Box component="span" data-testid="coverage-name" sx={{ ...base, fontSize: 16, lineHeight: '20px', fontWeight: 700, color: ANALYTICS.primary }}>
          {row.districtName || row.districtId}
        </Box>
        <Box component="span" data-testid="coverage-sub" data-warn={feedProblems(row) > 0 ? 'true' : undefined} sx={{ ...captionSx, color: feedProblems(row) > 0 ? ANALYTICS.red600 : ANALYTICS.muted }}>
          {`${fmt(row.students)} · ${describeCoverageSources(row.coverage)}${sharedCount(row) > 0 ? ` · ${plural(sharedCount(row), 'shared feed column')}` : ''}${placeholderCount(row) > 0 ? ` · ${plural(placeholderCount(row), 'placeholder name')}` : ''}`}
        </Box>
      </Box>
      {COVERAGE_FIELDS.map((field) => {
        const cell = coverageCell(row.coverage, field.key);
        const tint = coverageTint(field, cell, row.students);
        return (
          <Box key={field.key} data-testid="coverage-cell" data-field={field.key} data-tint={tint} sx={{ ...cellSx, ...TINT_COLORS[tint], fontWeight: tint === 'ok' ? 400 : 700 }}>
            <Box component="span" sx={hiddenSx}>
              {`${field.label} `}
            </Box>
            <Box component="span" data-testid="coverage-cell-value">
              {coverageCellText(cell, row.students)}
            </Box>
          </Box>
        );
      })}
      <Chevron open={open} />
    </Box>
  );
}

function FieldRows({ row, group }: { row: AuditDistrict; group: CoverageGroup }) {
  return (
    <>
      <Typography data-testid="coverage-group" sx={{ ...eyebrowSx, color: ANALYTICS.primary, pt: group === 'every' ? '16px' : '24px', pb: '4px' }}>
        {GROUP_LABELS[group]}
      </Typography>
      {COVERAGE_FIELDS.filter((field) => field.group === group).map((field) => {
        const cell = coverageCell(row.coverage, field.key);
        const tint = coverageTint(field, cell, row.students);
        const absent = cell.source === 'absent' || cell.source === 'empty';
        const width = absent ? 0 : (share(cell.filled, row.students) ?? 0);
        const sourceTint: CoverageTint = absent ? tint : 'ok';
        return (
          <Box key={field.key} data-testid="coverage-field-row" data-field={field.key} sx={{ display: 'flex', alignItems: 'center', gap: '16px', py: '8px', borderBottom: `1px solid ${ANALYTICS.grey50}` }}>
            <Typography data-testid="coverage-field-label" sx={{ ...lineSx, color: ANALYTICS.ink, width: 280, flexShrink: 0 }}>
              {field.label}
            </Typography>
            <Box sx={{ flex: 1, height: 12, borderRadius: '6px', bgcolor: ANALYTICS.grey50, overflow: 'hidden' }}>
              <Box style={{ width: `${width}%` }} sx={{ height: 12, borderRadius: '6px', bgcolor: ANALYTICS.primary }} />
            </Box>
            <Typography data-testid="coverage-field-count" sx={{ ...lineSx, width: 160, flexShrink: 0, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: tint === 'red' ? ANALYTICS.red600 : ANALYTICS.ink }}>
              {`${fmt(cell.filled)} of ${fmt(row.students)}`}
            </Typography>
            <Typography data-testid="coverage-field-source" data-tint={sourceTint} sx={{ ...lineSx, width: 220, flexShrink: 0, fontWeight: sourceTint === 'ok' ? 400 : 700, color: sourceTint === 'ok' ? ANALYTICS.muted : TINT_COLORS[sourceTint].color }}>
              {COVERAGE_SOURCE_LABELS[cell.source]}
            </Typography>
          </Box>
        );
      })}
    </>
  );
}

function sharedCount(row: AuditDistrict): number {
  return row.rosterStream?.sharedColumns?.length ?? 0;
}

function placeholderCount(row: AuditDistrict): number {
  return row.rosterStream?.placeholderNames?.length ?? 0;
}

function feedProblems(row: AuditDistrict): number {
  return sharedCount(row) + placeholderCount(row);
}

function Detail({ row, onOpenDetails }: { row: AuditDistrict; onOpenDetails: () => void }) {
  const shared = describeSharedColumns(row.rosterStream?.sharedColumns);
  const placeholders = placeholderNamesLine(row.rosterStream?.placeholderNames, row.rosterStream?.placeholderStudents ?? 0, row.rosterStream?.used === true);
  return (
    <Box data-testid="coverage-detail" data-district={row.districtId} sx={{ display: 'flex', flexDirection: 'column', gap: '16px', p: '20px 24px 24px', bgcolor: ANALYTICS.grey25, borderBottom: `1px solid ${ANALYTICS.grey50}` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}>
        <Typography data-testid="coverage-detail-head" sx={lineSx}>
          {describeCoverageHeader(row)}
        </Typography>
        <Box
          component="button"
          type="button"
          data-testid="coverage-open-details"
          onClick={onOpenDetails}
          sx={{ ...base, fontSize: 16, lineHeight: '24px', fontWeight: 700, color: ANALYTICS.primary, background: 'none', border: 0, p: 0, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          Open district details
        </Box>
      </Box>
      {shared.length > 0 && (
        <Typography data-testid="coverage-shared-columns" sx={{ ...lineSx, color: ANALYTICS.red600 }}>
          {`${shared.join(' ')} Change ${shared.length > 1 ? 'them' : 'one of them'} on the SIS matching page.`}
        </Typography>
      )}
      {placeholders !== '' && (
        <Typography data-testid="coverage-placeholder-names" sx={{ ...lineSx, color: ANALYTICS.red600 }}>
          {placeholders}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', py: '8px', borderBottom: `1px solid ${ANALYTICS.ink}` }}>
          <Box sx={{ ...eyebrowSx, width: 280, flexShrink: 0 }}>SIS field</Box>
          <Box sx={{ ...eyebrowSx, flex: 1 }}>Students with a value</Box>
          <Box sx={{ ...eyebrowSx, width: 160, flexShrink: 0, textAlign: 'right' }}>Count</Box>
          <Box sx={{ ...eyebrowSx, width: 220, flexShrink: 0 }}>Source</Box>
        </Box>
        <FieldRows row={row} group="every" />
        <FieldRows row={row} group="some" />
      </Box>
    </Box>
  );
}

function Legend() {
  const swatch = (color: string) => <Box component="i" sx={{ display: 'inline-block', width: 16, height: 16, borderRadius: '4px', border: `1px solid ${ANALYTICS.grey50}`, bgcolor: color, flexShrink: 0 }} />;
  const itemSx = { display: 'flex', gap: '8px', alignItems: 'center' } as const;
  return (
    <Box data-testid="coverage-legend" sx={{ ...captionSx, display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
      <Box component="span" sx={itemSx}>
        {swatch(ANALYTICS.red100)}
        Missing a field every student should have
      </Box>
      <Box component="span" sx={itemSx}>
        {swatch(ANALYTICS.yellow100)}
        Thin, or a field some students should have is absent
      </Box>
      <Box component="span">Percent is students with a value, of students in district data</Box>
    </Box>
  );
}

export function SisCoverage({ districts, onOpenDetails }: Props) {
  const [open, setOpen] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<CoverageFilter>('all');
  const ranked = rankCoverage(districts);
  const visible = filterCoverage(ranked, query, filter);
  const uncounted = districts.filter((row) => row.coverage === undefined);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '24px', flexWrap: 'wrap' }}>
        <Summary rows={ranked} uncounted={uncounted} />
        <Controls query={query} filter={filter} onQuery={setQuery} onFilter={setFilter} />
      </Box>
      <Box component="section" aria-label="SIS data coverage by district" sx={cardSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <GridHead />
          {visible.length === 0 && (
            <Typography data-testid="coverage-empty" sx={{ ...lineSx, fontSize: 16, lineHeight: '24px', py: '32px' }}>
              No district matches.
            </Typography>
          )}
          {visible.map((entry) => {
            const isOpen = open === entry.row.districtId;
            return (
              <Box key={entry.row.districtId} sx={{ display: 'contents' }}>
                <Row entry={entry} open={isOpen} onToggle={() => setOpen(isOpen ? null : entry.row.districtId)} />
                {isOpen && <Detail row={entry.row} onOpenDetails={() => onOpenDetails(entry.row.districtId)} />}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Legend />
    </Box>
  );
}
